/**
 * «Вы заказывали»: список товаров, которые пользователь заказывал ранее,
 * отсортированный по популярности (в скольких заказах встречался товар).
 *
 * Персонального эндпоинта нет — агрегируем по истории заказов на клиенте и
 * резолвим в актуальные узлы меню текущего филиала (single / группа / комбо),
 * чтобы переиспользовать карточку каталога (ProductCatalogSection): бейджи,
 * избранное, открытие карточки товара, выбор размера/добавок.
 */

import { ref } from 'vue'
import { consola } from 'consola'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { buildMenuNodeIndex, resolveBranchAliases } from '@entities/product'
import { apiOrder } from '@entities/order'

const logger = consola.withTag('ordered-products')

/** Сколько последних заказов учитывать при подсчёте популярности */
const HISTORY_LIMIT = 50
/** Сколько товаров показывать */
const TOP_N = 15

export function useOrderedProducts() {
  const institutionStore = useInstitutionStore()
  const userProfileStore = useUserProfileStore()

  const items = ref<any[]>([])
  /** Самый последний заказ пользователя (для карточки «Повторить последний заказ») */
  const lastOrder = ref<any | null>(null)
  const isLoading = ref(false)

  /**
   * Индексы меню — общий кирпич entities/product: id варианта/товара/комбо →
   * узел меню, и id узла → узел. Заказ ссылается на конкретный id варианта,
   * а показать и открыть нужно родительский узел (группу/одиночный/комбо).
   */
  function buildNodeIndex(): { byProductId: Map<string, any>; byNodeId: Map<string, any> } {
    return buildMenuNodeIndex(institutionStore.products)
  }

  /** Узел доступен (есть хотя бы один не-стопнутый вариант / не в стоп-листе) */
  function isNodeAvailable(node: any): boolean {
    if (node.type === 'group' && Array.isArray(node.variants)) {
      return node.variants.some((variant: any) => variant?.stop_list !== true)
    }
    return node.stop_list !== true && node.is_stopped !== true && node.is_active !== false
  }

  /** id товара/комбо из позиции заказа (учёт разных вариантов полей) */
  function resolveProductId(item: any): string {
    return String(item.productId ?? item.comboId ?? item.combo_id ?? item.product_id ?? item.id ?? '')
  }

  /**
   * Узлы меню текущего филиала для товаров из истории, которых в этом меню нет.
   *
   * Карточка товара принадлежит филиалу, поэтому блюдо, заказанное на другой точке,
   * по своему id здесь не находится — и раньше просто выпадало из «вы заказывали».
   * Сервер приводит id к карточкам этого филиала через группы «одно блюдо».
   *
   * @returns карта «id из истории → узел меню текущего филиала»
   */
  async function buildAliasNodes(
    orders: any[],
    byProductId: Map<string, any>,
    branchId: number,
  ): Promise<Map<string, any>> {
    const aliasNodes = new Map<string, any>()

    const foreignIds = [
      ...new Set(
        orders
          .flatMap(order => (order?.items || []) as any[])
          // Комбо резолвятся своей привязкой к филиалу (combo_branches), не группами блюд
          .filter(item => !item?.comboId && !item?.combo_id)
          .map(item => resolveProductId(item))
          .filter(id => id && !byProductId.has(id)),
      ),
    ]

    // Общий резолв entities/product: сбой сети внутри, пустой ответ = как раньше
    const resolved = await resolveBranchAliases(foreignIds, branchId)

    for (const entry of resolved) {
      if (!entry.product_id) continue
      const node = byProductId.get(entry.product_id)
      // Узел нужен целиком (для карточки каталога): если резолвнутой карточки
      // в меню нет — показать нечего, пропускаем.
      if (node) aliasNodes.set(entry.requested, node)
    }

    logger.debug('resolved foreign products for branch', {
      requested: foreignIds.length,
      resolved: aliasNodes.size,
    })

    return aliasNodes
  }

  /** Считает частоту узлов по истории (distinct-заказы) */
  function countFrequency(
    orders: any[],
    byProductId: Map<string, any>,
    aliasNodes: Map<string, any>,
  ): Map<string, number> {
    const freq = new Map<string, number>()
    for (const order of orders) {
      const seen = new Set<string>()
      for (const item of (order?.items || []) as any[]) {
        const productId = resolveProductId(item)
        const node = byProductId.get(productId) ?? aliasNodes.get(productId)
        if (!node) continue
        const nodeId = String(node.id)
        if (seen.has(nodeId)) continue // в рамках одного заказа считаем один раз
        seen.add(nodeId)
        freq.set(nodeId, (freq.get(nodeId) || 0) + 1)
      }
    }
    return freq
  }

  /** Загружает и строит список «Вы заказывали» */
  async function load(): Promise<void> {
    if (isLoading.value) return
    if (!userProfileStore.getUserId) return
    const branchId = institutionStore.selectedBranch?.id
    if (!branchId) return

    isLoading.value = true
    try {
      // Меню нужно для резолва — и оно обязано быть МЕНЮ ЭТОГО ФИЛИАЛА.
      //
      // Раньше условием было «меню пустое». После смены филиала оно непустое — от прошлой
      // точки, и список собирался по нему: на Советской предлагался набор, который продаётся
      // только на Широкой, с её же ценой. Клик открывал конструктор с чужими блюдами, и
      // отказ человек получал только на кнопке «Добавить». Store перезагружает меню своим
      // watch'ом на филиал, но оба наблюдателя срабатывают на одно изменение — порядок
      // не гарантирован, поэтому сверяемся с филиалом меню, а не с его пустотой.
      const menuBranchId = institutionStore.productsBranchId
      if (!institutionStore.products?.length || Number(menuBranchId) !== Number(branchId)) {
        await institutionStore.fetchProducts(branchId, userProfileStore.getUserId)
      }

      const response = await apiOrder.getOrderHistory({
        userId: String(userProfileStore.getUserId),
        limit: HISTORY_LIMIT,
        offset: 0,
      })
      const orders = response?.success ? response.orders || [] : []

      // Самый свежий заказ (бэкенд сортирует created_at DESC)
      lastOrder.value = orders[0] ?? null

      const { byProductId, byNodeId } = buildNodeIndex()
      const aliasNodes = await buildAliasNodes(orders, byProductId, branchId)
      const freq = countFrequency(orders, byProductId, aliasNodes)

      items.value = [...freq.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([nodeId]) => byNodeId.get(nodeId))
        .filter(node => node && isNodeAvailable(node))
        .slice(0, TOP_N)

      logger.debug('ordered products built', { count: items.value.length })
    } catch (error) {
      logger.error('failed to load ordered products', error)
      items.value = []
      lastOrder.value = null
    } finally {
      isLoading.value = false
    }
  }

  /** Очистить (например при выходе из аккаунта) */
  function reset(): void {
    items.value = []
    lastOrder.value = null
  }

  return { items, lastOrder, isLoading, load, reset }
}
